import { HELPER_LABELS, type Helpers } from '../helpers.js';

export interface HelperPanelProps {
  readonly helpers: Helpers;
  readonly onChange: (helpers: Helpers) => void;
  readonly showTerritory: boolean;
  readonly onToggleTerritory: (on: boolean) => void;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * The training wheels, and the switch for taking them off.
 *
 * Every one of these answers something a stronger player reads at a glance, so
 * leaving them on forever would be the failure case. They are laid out as plain
 * checkboxes with a line of explanation each, because a learner should be able
 * to tell what a helper is for before turning it on rather than after.
 *
 * The whole panel folds away, since it is a thing you set and then stop looking
 * at. Collapsed it still reports how many are on, so nothing switched on can be
 * forgotten about behind a closed lid.
 */
export function HelperPanel({
  helpers,
  onChange,
  showTerritory,
  onToggleTerritory,
  open,
  onOpenChange,
}: HelperPanelProps) {
  const active = Object.values(helpers).filter(Boolean).length + (showTerritory ? 1 : 0);
  const total = HELPER_LABELS.length + 1; // the labelled helpers, plus territory

  /**
   * Every helper at once, or none of them.
   *
   * Driven off the keys of the object it was handed rather than a list written
   * out here, so a helper added later is covered without anyone remembering to
   * come back for it.
   */
  const setAll = (on: boolean) => {
    const next = { ...helpers } as Record<keyof Helpers, boolean>;
    for (const key of Object.keys(next) as (keyof Helpers)[]) next[key] = on;
    onChange(next);
    onToggleTerritory(on);
  };

  return (
    <details
      className="helpers"
      open={open}
      onToggle={(event) => onOpenChange((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        <span className="helpers-title">Helpers</span>
        <span className="helpers-actions">
          <span className="muted small">{active} on</span>
          {/*
            These sit inside the summary, which is a button, so both events have
            to be stopped or every press would also open or close the panel.
          */}
          <button
            type="button"
            className="link-button"
            disabled={active === total}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setAll(true);
            }}
          >
            All
          </button>
          <button
            type="button"
            className="link-button"
            disabled={active === 0}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setAll(false);
            }}
          >
            None
          </button>
        </span>
      </summary>

      <p className="muted small">
        Things a stronger player would see without being told. Turn them off as you stop needing
        them — that is what progress looks like here.
      </p>

      <ul className="helper-list">
        {HELPER_LABELS.map((entry) => (
          <li key={entry.key}>
            <label>
              <input
                type="checkbox"
                checked={helpers[entry.key]}
                onChange={(event) => onChange({ ...helpers, [entry.key]: event.target.checked })}
              />
              <span>
                <strong>{entry.label}</strong>
                <span className="muted small">{entry.hint}</span>
              </span>
            </label>
          </li>
        ))}

        {/* Territory lives here too, so every board overlay is in one place. */}
        <li>
          <label>
            <input
              type="checkbox"
              checked={showTerritory}
              onChange={(event) => onToggleTerritory(event.target.checked)}
            />
            <span>
              <strong>Territory</strong>
              <span className="muted small">
                Shades who currently surrounds what. An estimate, and it assumes every stone is
                alive.
              </span>
            </span>
          </label>
        </li>
      </ul>
    </details>
  );
}
